import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import Chart from 'react-apexcharts';
import { FaEdit } from 'react-icons/fa';
import SideBar from '../Stores/Insight/sideBar';
import { GetOneUser } from '../../authService/api';

const UserProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [profilePic, setProfilePic] = useState('https://via.placeholder.com/150');
  const [editPic, setEditPic] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [user, setUser] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    companyName: 'TATA Chroma',
    address: 'Bhiwandi, Maharashtra 421305',
    stats: {
      onlineDays: 20,
      offlineDays: 10,
      trespasser: 5,
      frisked: 3,
      notFrisked: 2
    }
  });

  // Keeping the existing useEffect and data fetching logic...
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const username = localStorage.getItem('username') || sessionStorage.getItem('username') || localStorage.getItem('user')?.username;

        if (!username) {
          setError('Please log in to view your profile');
          setIsLoading(false);
          return;
        }

        const response = await GetOneUser({ username });

        if (!response?.data?.data) {
          throw new Error('Could not load user data');
        }

        const userData = response.data.data;

        setUser((prevUser) => ({
          ...prevUser,
          name: userData.firstname && userData.lastname ? `${userData.firstname} ${userData.lastname}` : userData.username,
          role: userData.role,
          email: userData.email,
          phone: userData.number
        }));
      } catch (err) {
        setError(err.message === 'No username found' ? 'Please log in to view your profile' : 'Failed to load user details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  const onlineOfflineData = {
    series: [user.stats.onlineDays, user.stats.offlineDays],
    options: {
      chart: {
        type: 'pie',
        fontFamily: 'Inter, sans-serif',
        background: 'transparent'
      },
      labels: ['Online Days', 'Offline Days'],
      colors: ['#10B981', '#EF4444'],
      legend: {
        position: 'bottom',
        labels: {
          colors: '#4B5563'
        }
      },
      stroke: {
        width: 0
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(val)}%`,
        style: {
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif'
        }
      }
    }
  };

  const trespasserData = {
    series: [
      {
        name: 'Count',
        data: [user.stats.trespasser, user.stats.frisked, user.stats.notFrisked]
      }
    ],
    options: {
      chart: {
        type: 'bar',
        fontFamily: 'Inter, sans-serif',
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '60%'
        }
      },
      xaxis: {
        categories: ['Trespasser', 'Frisked', 'Not Frisked'],
        labels: {
          style: {
            colors: '#4B5563',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#4B5563',
            fontSize: '12px'
          }
        }
      },
      colors: ['#3B82F6'],
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4
      }
    }
  };

  return (
    <Grid container className="min-h-screen bg-gray-50">
      <Grid item xs={12} md={2}>
        <SideBar />
      </Grid>

      <Grid item xs={12} md={10} className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <CircularProgress className="text-blue-500" />
          </div>
        ) : error ? (
          <div className="text-red-500 text-center mt-4 p-4 bg-red-50 rounded-lg">{error}</div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="container mx-auto"
          >
            <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center">
                      <div className="relative mb-6 group">
                        <Avatar src={profilePic} className="w-24 h-24 ring-4 ring-blue-50" sx={{ width: 96, height: 96 }} />
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="absolute -bottom-2 -right-2 p-2 bg-white rounded-full shadow-lg cursor-pointer"
                          onClick={() => setIsEditing(true)}
                        >
                          <FaEdit className="text-blue-500" size={20} />
                        </motion.div>
                      </div>
                      <Typography variant="h4" className="text-gray-800 font-bold mb-2">
                        {user.name}
                      </Typography>
                      <Typography variant="h6" className="text-blue-500 font-medium mb-3">
                        {user.role}
                      </Typography>
                      <div className="space-y-2 text-center">
                        <Typography className="text-gray-600">{user.email}</Typography>
                        <Typography className="text-gray-600">{user.phone}</Typography>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100 h-full">
                  <CardContent className="p-6">
                    <Typography variant="h5" className="text-gray-800 font-bold mb-4">
                      Company Details
                    </Typography>
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <Typography className="text-gray-600 font-medium mb-1">Company Name</Typography>
                        <Typography className="text-gray-800">{user.companyName}</Typography>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <Typography className="text-gray-600 font-medium mb-1">Address</Typography>
                        <Typography className="text-gray-800">{user.address}</Typography>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </Box>

            <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
                  <CardContent className="p-6">
                    <Typography variant="h6" className="text-gray-800 font-bold mb-4">
                      Active Status
                    </Typography>
                    <Chart options={onlineOfflineData.options} series={onlineOfflineData.series} type="pie" height={300} />
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
                  <CardContent className="p-6">
                    <Typography variant="h6" className="text-gray-800 font-bold mb-4">
                      Frisked Statistics
                    </Typography>
                    <Chart options={trespasserData.options} series={trespasserData.series} type="bar" height={300} />
                  </CardContent>
                </Card>
              </motion.div>
            </Box>

            <Dialog
              open={isEditing}
              onClose={() => setIsEditing(false)}
              PaperProps={{
                className: 'rounded-xl',
                style: { padding: '24px' }
              }}
            >
              <DialogTitle className="text-gray-800 font-bold">Update Profile Picture</DialogTitle>
              <DialogContent>
                <Box className="mt-4 space-y-4">
                  <TextField
                    label="Profile Picture URL"
                    variant="outlined"
                    value={editPic}
                    onChange={(e) => setEditPic(e.target.value)}
                    fullWidth
                    className="bg-gray-50"
                  />
                  <div className="flex justify-end space-x-2">
                    <Button onClick={() => setIsEditing(false)} variant="outlined" color="inherit">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setProfilePic(editPic);
                        setEditPic('');
                        setIsEditing(false);
                      }}
                      variant="contained"
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Save Changes
                    </Button>
                  </div>
                </Box>
              </DialogContent>
            </Dialog>
          </motion.div>
        )}
      </Grid>
    </Grid>
  );
};

export default UserProfile;
